import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, Users, BarChart, Shield, ArrowRight, CheckIcon, XIcon } from 'lucide-react';

// IDA Needs Assessment (10Q) - Nach Spezifikation
const BedarfsanalyseForm = ({ data, setData, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const assessmentRef = useRef(null);

  // 10 Fragen nach Spezifikation
  const questions = [
    {
      id: "q1_industry",
      label: "In welcher Branche sind Sie tätig?",
      type: "single_select",
      options: [
        { value: "spielhalle", label: "Spielhalle", points: 35 },
        { value: "wettbuero", label: "Wettbüro", points: 35 },
        { value: "gastronomy", label: "Gastronomie mit Geldspielgeräten", points: 30 },
        { value: "events", label: "Veranstaltungen", points: 30 },
        { value: "casino", label: "Casino", points: 35 },
        { value: "other", label: "Sonstige", points: 20 }
      ]
    },
    {
      id: "q2_locations",
      label: "Wie viele Standorte oder Einsatzorte betreiben Sie?",
      type: "single_select",
      options: [
        { value: "1", label: "1", points: 10 },
        { value: "2_5", label: "2–5", points: 20 },
        { value: "6_20", label: "6–20", points: 30 },
        { value: "20_plus", label: "20+", points: 40 }
      ]
    },
    {
      id: "q3_frequency",
      label: "Wie häufig müssen interne Kontrollen/Dokumentationen durchgeführt werden?",
      type: "single_select",
      options: [
        { value: "daily", label: "täglich", points: 40 },
        { value: "weekly", label: "wöchentlich", points: 30 },
        { value: "monthly", label: "monatlich", points: 20 },
        { value: "rare", label: "selten", points: 10 }
      ]
    },
    {
      id: "q4_current_method",
      label: "Wie dokumentieren Sie aktuell Ihre Kontrollen?",
      type: "single_select",
      options: [
        { value: "paper", label: "Papier/Formulare", points: 10 },
        { value: "excel", label: "Excel/Spreadsheets", points: 20 },
        { value: "intranet", label: "Intranet/Fileshares", points: 30 },
        { value: "system", label: "Systembasiert (Software)", points: 40 }
      ]
    },
    {
      id: "q5_reminders",
      label: "Gibt es Erinnerungen/Eskalation, wenn Aufgaben nicht erledigt werden?",
      type: "single_select",
      options: [
        { value: "no", label: "Nein", points: 40 },
        { value: "partial", label: "Teilweise", points: 20 },
        { value: "yes", label: "Ja", points: 10 }
      ]
    },
    {
      id: "q6_external_proof",
      label: "Müssen Sie regelmäßig Nachweise für externe Stellen erbringen?",
      type: "single_select",
      options: [
        { value: "none", label: "Nein", points: 10 },
        { value: "seldom", label: "Selten", points: 20 },
        { value: "regular", label: "Regelmäßig", points: 40 }
      ]
    },
    {
      id: "q7_staff_rotation",
      label: "Wie stark ist Fluktuation oder Schichtrotation?",
      type: "single_select",
      options: [
        { value: "low", label: "Gering", points: 10 },
        { value: "mid", label: "Mittel", points: 20 },
        { value: "high", label: "Hoch", points: 40 }
      ]
    },
    {
      id: "q8_auditability",
      label: "Wie wichtig ist revisionssichere, rechtssichere Dokumentation?",
      type: "single_select",
      options: [
        { value: "low", label: "Unwichtig", points: 10 },
        { value: "mid", label: "Teilweise wichtig", points: 20 },
        { value: "high", label: "Sehr wichtig", points: 40 }
      ]
    },
    {
      id: "q9_training",
      label: "Gibt es regelmäßige Schulungs- oder Nachweispflichten für Mitarbeitende?",
      type: "single_select",
      options: [
        { value: "no", label: "Nein", points: 10 },
        { value: "partial", label: "Teilweise", points: 20 },
        { value: "yes", label: "Ja", points: 40 }
      ]
    },
    {
      id: "q10_painpoint",
      label: "Welche Herausforderung trifft am ehesten zu?",
      type: "single_select",
      options: [
        { value: "proof_gaps", label: "Fehlende Nachweise gegenüber Dritten", points: 25 },
        { value: "no_reminders", label: "Keine zuverlässigen Erinnerungssysteme", points: 25 },
        { value: "chaotic_flows", label: "Unübersichtliche Abläufe/Verantwortlichkeiten", points: 25 },
        { value: "no_standards", label: "Fehlende Standardisierung/Checklisten", points: 25 }
      ]
    }
  ];

  const totalQuestions = questions.length;
  const currentQuestion = questions[currentStep];
  const isLastQuestion = currentStep === totalQuestions - 1;

  const handleAnswer = (questionId, value, points) => {
    setData(prev => ({
      ...prev,
      [questionId]: { value, points }
    }));
  };

  const handleNext = () => {
    if (isLastQuestion) {
      // Berechne Ergebnis und zeige es direkt an
      const result = evaluateNewAssessment(data, {});
      onComplete(result);
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const canContinue = currentQuestion && data[currentQuestion.id];

  // Scroll-Position stabilisieren bei Step-Änderungen (nur nach Schritt 1)
  useEffect(() => {
    if (currentStep > 0 && assessmentRef.current) {
      // Kleine Verzögerung, um sicherzustellen, dass das DOM updated ist
      setTimeout(() => {
        assessmentRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }, 100);
    }
  }, [currentStep]);

  // Lead Capture Form wurde entfernt - jetzt direkt im Ergebnis integriert

  // Questions Form
  return (
    <div ref={assessmentRef} className="max-w-2xl mx-auto px-4 sm:px-0">
      {/* Header */}
      <div className="text-center mb-6 sm:mb-8">
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          Passt IDA zu Ihrem Betrieb?
        </h3>
        <p className="text-sm sm:text-base text-gray-600">
          10 Fragen · ca. 2 Minuten · individuelles Ergebnis
        </p>
      </div>

      {/* Progress Bar */}
      <div className="mb-6 sm:mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs sm:text-sm text-gray-600">Frage {currentStep + 1} von {totalQuestions}</span>
          <span className="text-xs sm:text-sm text-gray-600">{Math.round(((currentStep + 1) / totalQuestions) * 100)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentStep + 1) / totalQuestions) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Current Question */}
      <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200 shadow-sm">
        <h4 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6 text-center">
          {currentQuestion?.label}
        </h4>

        {/* Dropdown für erste Frage (Branche), Grid für andere Fragen */}
        {currentQuestion?.id === 'q1_industry' ? (
          <div className="space-y-4">
            <select
              value={data[currentQuestion.id]?.value || ''}
              onChange={(e) => {
                const selectedOption = currentQuestion.options.find(opt => opt.value === e.target.value);
                if (selectedOption) {
                  handleAnswer(currentQuestion.id, selectedOption.value, selectedOption.points);
                }
              }}
              className="w-full p-3 sm:p-4 border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-base sm:text-lg bg-white appearance-none cursor-pointer"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%232563eb'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundPosition: 'right 0.75rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.5em 1.5em',
                paddingRight: '2.5rem'
              }}
            >
              <option value="">-- Bitte wählen Sie Ihre Branche --</option>
              {currentQuestion.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {data[currentQuestion.id]?.value && (
              <div className="flex items-center text-green-600 text-xs sm:text-sm">
                <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                <span className="break-words">Branche ausgewählt: {currentQuestion.options.find(opt => opt.value === data[currentQuestion.id]?.value)?.label}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {currentQuestion?.options.map((option) => (
              <button
                key={option.value}
                onClick={() => handleAnswer(currentQuestion.id, option.value, option.points)}
                className={`p-3 sm:p-4 text-left border-2 rounded-lg transition-all ${
                  data[currentQuestion.id]?.value === option.value
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex justify-between items-center">
                  <span className="font-medium text-sm sm:text-base">{option.label}</span>
                  {data[currentQuestion.id]?.value === option.value && (
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 ml-2" />
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between gap-3 mt-6">
        <Button
          variant="outline"
          onClick={handleBack}
          disabled={currentStep === 0}
          className="px-4 sm:px-6 text-sm sm:text-base"
        >
          Zurück
        </Button>
        <Button
          onClick={handleNext}
          disabled={!canContinue}
          className="px-4 sm:px-6 bg-blue-600 hover:bg-blue-700 text-white text-sm sm:text-base flex items-center justify-center"
          data-testid="assessment-next-button"
        >
          {isLastQuestion ? (
            <span className="text-sm sm:text-base">Kontaktdaten eingeben</span>
          ) : (
            <>
              <span>Weiter</span>
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

// Neue Bewertungslogik nach 10Q Spezifikation mit detaillierten Ergebnissen
const evaluateNewAssessment = (assessmentData, leadData) => {
  // Berechne Raw Score aus allen Antworten
  let rawPoints = 0;
  const maxRawPoints = 380; // Aus der Spezifikation
  
  Object.values(assessmentData).forEach(answer => {
    if (answer && answer.points) {
      rawPoints += answer.points;
    }
  });

  // Skaliere auf 0-100
  const scaledScore = Math.round((rawPoints / maxRawPoints) * 100);

  // Bestimme Bucket mit neuen, detaillierten Texten (4 Buckets)
  let bucket = 'noneed';
  let bucketData = {
    headline: "IDA ist derzeit vermutlich nicht die passende Lösung",
    body: "Ihre Abläufe scheinen schlank und weitgehend ohne regelmäßige Nachweispflichten zu funktionieren. IDA ist aktuell möglicherweise nicht die passende Lösung, kann aber künftig relevant werden, falls Dokumentations- oder Kontrollpflichten zunehmen.",
    detailText: "Ihre aktuellen Prozesse scheinen bereits gut zu funktionieren und benötigen möglicherweise noch keine zusätzliche Compliance-Software.",
    insight: "💡 Beobachten Sie Ihre Prozesse und führen Sie den Test erneut durch, falls sich Ihre Anforderungen ändern.",
    recommendations: [
      "Optionales Beratungsgespräch vereinbaren",
      "Prozesse beobachten und bei Veränderung IDA-Test erneut durchführen"
    ],
    cta: "Beratungstermin vereinbaren",
    tonality: "neutral"
  };

  if (scaledScore >= 80) {
    bucket = 'high';
    bucketData = {
      headline: "IDA macht Sie revisionssicher und zukunftsfähig",
      body: "Ihre Organisation ist stark aufgestellt – jetzt geht es um Revisionssicherheit, Automatisierung und Skalierung. IDA liefert Nachweise, Audit-Logs und Erinnerungen in Echtzeit.",
      detailText: "Ihre Abläufe sind bereits gut organisiert – jetzt geht es um Revisionssicherheit und Automatisierung. IDA liefert digitale Nachweise, Audit-Logs, automatische Erinnerungen und Standort-Dashboards in Echtzeit.",
      insight: "💡 Ihr Vorteil: Sie sind jederzeit auditbereit, behalten alles im Blick und gewinnen wertvolle Zeit.",
      recommendations: [
        "Zentrale Zertifikatsverwaltung",
        "Audit-Logs & Compliance-Dashboards",
        "API-Integration ins bestehende System"
      ],
      cta: "Beratungstermin vereinbaren",
      tonality: "visionär"
    };
  } else if (scaledScore >= 50) {
    bucket = 'mid';
    bucketData = {
      headline: "IDA bringt Struktur und Kontrolle", 
      body: "Ihre Prozesse funktionieren, aber Verantwortlichkeiten und Nachweise sind noch nicht überall klar. IDA schafft Struktur, automatische Eskalationen und vollständige Transparenz.",
      detailText: "Ihre Prozesse funktionieren, aber Informationsflüsse und Verantwortlichkeiten sind noch nicht überall sauber dokumentiert. IDA schafft Struktur, automatische Eskalationen und klare Nachvollziehbarkeit.",
      insight: "💡 Ihr Vorteil: Weniger Suchaufwand, klare Zuständigkeiten und dokumentierte Sicherheit.",
      recommendations: [
        "Rollen- & Rechtekonzept aktivieren",
        "Eskalationslogik bei Fristversäumnissen",
        "Standort-Reporting einführen"
      ],
      cta: "Beratungstermin vereinbaren",
      tonality: "beratend"
    };
  } else if (scaledScore >= 25) {
    bucket = 'low';
    bucketData = {
      headline: "IDA erkennt Ihre Pain Points",
      body: "Viele Abläufe sind noch manuell oder unstrukturiert. IDA hilft Ihnen, Routinen zu digitalisieren, Verantwortlichkeiten zu klären und Entlastung zu schaffen.",
      detailText: "Viele Aufgaben laufen bei Ihnen manuell oder ohne klare Verantwortlichkeiten. IDA hilft Ihnen, Routinen zu strukturieren, Checklisten zu digitalisieren und wichtige Nachweise nicht mehr zu übersehen.",
      insight: "💡 Erster Schritt: Starten Sie mit wenigen digitalen Checklisten – IDA erinnert automatisch an offene Aufgaben.",
      recommendations: [
        "Start mit wenigen digitalen Checklisten",
        "Automatische Erinnerungen aktivieren", 
        "PDF-Reports zur Nachweisführung"
      ],
      cta: "Beratungstermin vereinbaren",
      tonality: "empathisch"
    };
  }

  return {
    type: 'assessment_complete',
    rawPoints,
    scaledScore,
    bucket,
    bucketData,
    leadData,
    assessmentData
  };
};

// Neue Ergebnis-Komponente nach 10Q Spezifikation mit integrierter Lead-Erfassung
const BedarfsanalyseResult = ({ result, onNewAnalysis, onRequestDemo }) => {
  const [leadData, setLeadData] = useState({
    lead_name: '',
    lead_email: '',
    lead_company: '',
    lead_phone: '',
    lead_optin: false,
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getResultColor = () => {
    switch (result.bucket) {
      case 'high': return 'green';
      case 'mid': return 'blue';
      case 'low': return 'orange';
      case 'noneed': return 'gray';
      default: return 'gray';
    }
  };

  const updateLeadData = (key, value) => {
    setLeadData(prev => ({ ...prev, [key]: value }));
  };

  const canSubmitLead = leadData.lead_name && leadData.lead_email && leadData.consent;

  const color = getResultColor();
  const { scaledScore, bucketData } = result;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Linke Spalte: Ergebnis */}
        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 shadow-sm">
        {/* Score Display */}
        <div className="text-center mb-8">
          <div className="relative inline-block mb-6">
            <div className="w-32 h-32 mx-auto rounded-full bg-gray-100 flex items-center justify-center relative overflow-hidden border-4 border-gray-200">
              <div 
                className={`absolute bottom-0 left-0 w-full bg-${color === 'green' ? 'green' : color === 'blue' ? 'blue' : 'orange'}-500 transition-all duration-1500`}
                style={{ height: `${scaledScore}%` }}
              ></div>
              <span className="relative z-10 text-3xl font-bold text-gray-900">{scaledScore}</span>
            </div>
            <p className="text-lg font-semibold text-gray-800 mb-1">Ihr IDA-Ergebnis: {scaledScore}/100</p>
            <p className="text-sm text-gray-500">0–100 (je höher, desto mehr Potenzial)</p>
          </div>

          <h3 className={`text-3xl font-bold mb-6 text-${color === 'green' ? 'green' : color === 'blue' ? 'blue' : 'orange'}-700`}>
            {bucketData.headline}
          </h3>
          
          {/* Detaillierte Beschreibung */}
          <div className="text-left mb-8">
            <p className="text-gray-700 text-lg leading-relaxed mb-4">
              {bucketData.detailText}
            </p>
            <div className={`bg-${color === 'green' ? 'green' : color === 'blue' ? 'blue' : 'orange'}-50 border-l-4 border-${color === 'green' ? 'green' : color === 'blue' ? 'blue' : 'orange'}-400 p-4 rounded-r-lg`}>
              <p className="text-gray-700 font-medium">
                {bucketData.insight}
              </p>
            </div>
          </div>
        </div>

        {/* Empfehlungen */}
        <div className="mb-8">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            Empfohlene nächste Schritte:
          </h4>
          <div className="space-y-3">
            {bucketData.recommendations.map((rec, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className={`w-6 h-6 rounded-full bg-${color === 'green' ? 'green' : color === 'blue' ? 'blue' : 'orange'}-500 flex items-center justify-center flex-shrink-0 mt-0.5`}>
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
                <span className="text-gray-700">{rec}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTAs */}
        <div className="space-y-4">
          <Button
            onClick={onRequestDemo}
            size="lg"
            className={`w-full bg-${color === 'green' ? 'green' : color === 'blue' ? 'blue' : 'orange'}-600 hover:bg-${color === 'green' ? 'green' : color === 'blue' ? 'blue' : 'orange'}-700 text-white font-semibold py-4 text-lg`}
          >
            {bucketData.cta}
          </Button>
          
          <div className="flex space-x-3">
            <Button
              onClick={onNewAnalysis}
              variant="outline"
              size="lg"
              className="flex-1"
            >
              Neue Analyse starten
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="flex-1 text-gray-600"
            >
              Ergebnis als PDF speichern
            </Button>
          </div>
        </div>

        </div>

        {/* Rechte Spalte: Lead-Erfassung */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl p-6 border border-blue-200">
          <h4 className="text-xl font-bold text-gray-900 mb-4">
            📧 Ergebnis per E-Mail erhalten
          </h4>
          <p className="text-gray-600 mb-6 text-sm">
            Lassen Sie sich Ihre Analyse zusenden und vereinbaren Sie optional ein persönliches Gespräch.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
              <input
                type="text"
                value={leadData.lead_name}
                onChange={(e) => updateLeadData('lead_name', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ihr vollständiger Name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">E-Mail *</label>
              <input
                type="email"
                value={leadData.lead_email}
                onChange={(e) => updateLeadData('lead_email', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="ihre.email@unternehmen.de"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Unternehmen</label>
              <input
                type="text"
                value={leadData.lead_company}
                onChange={(e) => updateLeadData('lead_company', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ihr Unternehmen"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Telefon (optional)</label>
              <input
                type="tel"
                value={leadData.lead_phone}
                onChange={(e) => updateLeadData('lead_phone', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="+49 123 456 7890"
              />
            </div>

            <div className="flex items-start space-x-3 pt-2">
              <input
                type="checkbox"
                checked={leadData.lead_optin}
                onChange={(e) => updateLeadData('lead_optin', e.target.checked)}
                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label className="text-sm text-gray-600">
                Ich möchte Neuigkeiten zu IDA erhalten.
              </label>
            </div>

            <div className="flex items-start space-x-3 pt-2">
              <input
                type="checkbox"
                checked={leadData.consent}
                onChange={(e) => updateLeadData('consent', e.target.checked)}
                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label className="text-sm text-gray-600">
                Ich stimme der Verarbeitung meiner Angaben gemäß{' '}
                <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                  Datenschutzerklärung
                </a>
                {' '}zu. *
              </label>
            </div>

            <div className="space-y-3 pt-4">
              <Button
                onClick={() => {
                  // Handle lead submission here
                  setIsSubmitting(true);
                  // Add API call logic here
                  setTimeout(() => setIsSubmitting(false), 1000);
                }}
                disabled={!canSubmitLead || isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3"
              >
                {isSubmitting ? 'Wird gesendet...' : 'Ergebnis per E-Mail erhalten'}
              </Button>

              <Button
                onClick={onRequestDemo}
                className={`w-full bg-${color === 'green' ? 'green' : color === 'blue' ? 'blue' : color === 'orange' ? 'orange' : 'gray'}-600 hover:bg-${color === 'green' ? 'green' : color === 'blue' ? 'blue' : color === 'orange' ? 'orange' : 'gray'}-700 text-white py-3`}
              >
                {bucketData.cta}
              </Button>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <Button
                onClick={onNewAnalysis}
                variant="outline"
                className="w-full"
              >
                Neue Analyse starten
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const IndustryCarousel = ({ onOpenDemo, showAssessment = false, assessmentData = {}, setAssessmentData = () => {}, assessmentResult = null, setAssessmentResult = () => {} }) => {
  const [activeTab, setActiveTab] = useState('spielhalle');

  const industries = {
    spielhalle: {
      id: 'spielhalle',
      name: 'Spielhalle',
      tagline: 'Komplette Compliance für Spielhallenbetreiber',
      shortDescription: 'Von der täglichen Jugendschutzkontrolle bis zur vollständigen Responsible-Gaming-Dokumentation.',
      icon: <Users className="w-8 h-8" />,
      image: '/images/slot-machine.jpg',
      features: [
        {
          title: 'ToDos klar im Griff',
          description: 'Automatische Aufgabenverwaltung und Erinnerungen für alle täglichen Compliance-Aufgaben'
        },
        {
          title: 'Jugendschutz sicher dokumentiert',
          description: 'Lückenlose Kontrolle und Dokumentation aller Jugendschutzmaßnahmen'
        },
        {
          title: 'Problematisches Spielverhalten erkennen',
          description: 'Responsible Gaming Maßnahmen professionell verwalten und dokumentieren'
        },
        {
          title: 'Mitarbeiterschulungen & Zertifikate',
          description: 'Schulungsnachweis und Zertifikatsverwaltung für alle Mitarbeiter'
        }
      ]
    },
    wettbuero: {
      id: 'wettbuero',
      name: 'Wettbüro',
      tagline: 'Erweiterte Compliance mit Anti-Geldwäsche',
      shortDescription: 'Alle Spielhallen-Features ergänzt durch spezialisierte AML- und GwG-Funktionen für umfassende Geldwäsche-Prävention.',
      icon: <BarChart className="w-8 h-8" />,
      image: '/images/wettshop.png',
      features: [
        {
          title: 'Alle Spielhallen-Features',
          description: 'ToDos, Jugendschutz, Responsible Gaming und Mitarbeiterschulungen inklusive'
        },
        {
          title: 'Geldwäschemeldungen sicher erfassen',
          description: 'Sichere Dokumentation und Erfassung verdächtiger Transaktionen nach GwG'
        },
        {
          title: 'Anti-Money-Laundering (AML)',
          description: 'Automatische Überwachung und Compliance-Berichte für Aufsichtsbehörden'
        },
        {
          title: 'Erweiterte Dokumentation',
          description: 'Spezielle Berichte und Meldungen für Regulierungsbehörden'
        }
      ]
    },
    gastronomie: {
      id: 'gastronomie',
      name: 'Gastronomie',
      tagline: 'HACCP und Spielerschutz für Gastronomen',
      shortDescription: 'Lebensmittelhygiene, Alkoholausschank und optionaler Spielerschutz in einer Lösung.',
      icon: <Shield className="w-8 h-8" />,
      image: '/images/restaurant-interior.jpg',
      features: [
        {
          title: 'HACCP im Griff',
          description: 'Lebensmittelhygiene und Temperaturkontrollen systematisch dokumentieren'
        },
        {
          title: 'Mitarbeiterschulungen',
          description: 'Hygieneschulungen und Compliance-Training für alle Mitarbeiter'
        },
        {
          title: 'Jugendschutz bei Alkohol',
          description: 'Kontrolle und Dokumentation bei Alkoholausschank und Geldspielgeräten'
        },
        {
          title: 'Spielerschutz',
          description: 'Verantwortungsvoller Umgang mit Geldspielgeräten in der Gastronomie'
        }
      ]
    }
  };

  const currentIndustry = industries[activeTab];

  return (
    <div className="w-full">
      {/* Tab Navigation - Minimalistisch mit Underline */}
      <div className="flex flex-wrap justify-center mb-12 gap-8" role="tablist" aria-label="Branchen-Auswahl">
        {Object.values(industries).map((industry) => (
          <button
            key={industry.id}
            id={`tab-${industry.id}`}
            onClick={() => setActiveTab(industry.id)}
            role="tab"
            aria-selected={activeTab === industry.id ? "true" : "false"}
            aria-controls={`panel-${industry.id}`}
            className={`pb-3 text-xl font-bold transition-all duration-300 relative ${
              activeTab === industry.id
                ? 'text-teal-700'
                : 'text-gray-700 hover:text-teal-700'
            }`}
          >
            {industry.name}
            {activeTab === industry.id && (
              <span className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-700 to-blue-500 rounded-full"></span>
            )}
          </button>
        ))}
      </div>

      {/* Content Area - Minimalistisches 50/50 Layout */}
      <Card
        id={`panel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`tab-${activeTab}`}
        className="overflow-hidden border-2 border-gray-200 shadow-lg">
        <CardContent className="p-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[500px]">

            {/* Left Side - Bild mit Text-Overlay oben */}
            <div className="relative overflow-hidden">
              <img
                src={currentIndustry.image}
                alt={`${currentIndustry.name} - ${currentIndustry.tagline}`}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />

              {/* Content Overlay - nur oben mit grünem Hintergrund */}
              <div className="relative z-10 flex flex-col justify-start h-full text-white">
                <div className="bg-gradient-to-br from-teal-700 to-teal-900 opacity-95 p-8 lg:p-10">
                  <h3 className="text-3xl lg:text-4xl font-bold mb-4 leading-tight">{currentIndustry.name}</h3>
                  <p className="text-base font-bold mb-1">{currentIndustry.tagline}</p>
                  <p className="text-base leading-relaxed">{currentIndustry.shortDescription}</p>
                </div>
              </div>
            </div>

            {/* Right Side - Features und CTA */}
            <div className="bg-gray-50 p-8 lg:p-10 flex flex-col">

              {/* Features Header */}
              <h4 className="text-2xl font-bold text-gray-900 mb-6">
                Ihre Vorteile im Detail
              </h4>

              {/* Features List - Mit Icon-Box wie Schulungscenter */}
              <div className="space-y-4 flex-grow">
                {currentIndustry.features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-4 group">
                    <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 group-hover:bg-teal-200 transition-colors">
                      <svg className="w-5 h-5 text-teal-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h5 className="text-base font-semibold text-gray-900 mb-1">
                        {feature.title}
                      </h5>
                      <p className="text-gray-700 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA Section */}
              <div className="mt-8">
                <Link to="/demo-anfrage" className="block">
                  <Button
                    size="lg"
                    className="w-full bg-teal-700 hover:bg-teal-800 text-white font-semibold py-4 text-base"
                  >
                    Demo anfragen
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Indicator Dots - Minimalistisch */}
      <div className="flex justify-center mt-8 space-x-3" role="group" aria-label="Branchen-Indikatoren">
        {Object.values(industries).map((industry) => (
          <button
            key={industry.id}
            onClick={() => setActiveTab(industry.id)}
            aria-label={`Zur ${industry.name} Branche wechseln`}
            aria-current={activeTab === industry.id ? 'true' : 'false'}
            className={`transition-all duration-300 rounded-full ${
              activeTab === industry.id
                ? 'w-12 h-3 bg-teal-600 shadow-md'
                : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// Export assessment components for use in HomePage
export { BedarfsanalyseForm, BedarfsanalyseResult, evaluateNewAssessment };